import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useMemo } from 'react';
import {
  Tags,
  ShoppingCart,
  Package,
  ShoppingBag,
  Clock,
  Euro,
  ChevronRight,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { formatDistanceToNow, isToday, isYesterday, format } from 'date-fns';

// Types
interface Stats {
  products: number;
  labels: number;
  orders: number;
  contacts: number;
  revenue: number;
}

interface ActivityItem {
  id: string;
  type: 'order' | 'product' | 'label';
  title: string;
  subtitle: string;
  createdAt: string;
  meta?: {
    status?: string;
    storeName?: string;
  };
}

// Get greeting based on time
function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

// Format date for grouping
function formatDateGroup(date: Date): string {
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  return format(date, 'MMMM d');
}

// Group activities by date
function groupActivities(activities: ActivityItem[]) {
  const groups: Record<string, ActivityItem[]> = {};
  
  activities.forEach((item) => {
    const date = new Date(item.createdAt);
    const key = formatDateGroup(date);
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  });
  
  return groups;
}

// Get link for activity item
function getActivityLink(item: ActivityItem): string {
  switch (item.type) {
    case 'order':
      return '/shop/orders';
    case 'product':
      return '/shop/products';
    case 'label':
      return '/labels';
  }
}

// Activity icon component
function ActivityIcon({ type }: { type: ActivityItem['type'] }) {
  const iconClass = "w-4 h-4";
  
  switch (type) {
    case 'order':
      return (
        <div className="w-8 h-8 rounded-full bg-orange-50 border border-orange-200 flex items-center justify-center group-hover:bg-orange-100 group-hover:border-orange-300 transition-colors">
          <ShoppingBag className={`${iconClass} text-orange-600`} />
        </div>
      );
    case 'product':
      return (
        <div className="w-8 h-8 rounded-full bg-slate-50 border border-slate-200 flex items-center justify-center group-hover:bg-slate-100 group-hover:border-slate-300 transition-colors">
          <Package className={`${iconClass} text-slate-600`} />
        </div>
      );
    case 'label':
      return (
        <div className="w-8 h-8 rounded-full bg-green-50 border border-green-200 flex items-center justify-center group-hover:bg-green-100 group-hover:border-green-300 transition-colors">
          <Tags className={`${iconClass} text-green-600`} />
        </div>
      );
  }
}

// Activity type label
function getActivityLabel(type: ActivityItem['type']): string {
  switch (type) {
    case 'order': return 'New order';
    case 'product': return 'Product created';
    case 'label': return 'Label created';
  }
}

// Status badge for orders
function OrderStatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    PENDING: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    CONFIRMED: 'bg-blue-50 text-blue-700 border-blue-200',
    PROCESSING: 'bg-purple-50 text-purple-700 border-purple-200',
    SHIPPED: 'bg-sky-50 text-sky-700 border-sky-200',
    DELIVERED: 'bg-green-50 text-green-700 border-green-200',
    CANCELLED: 'bg-red-50 text-red-700 border-red-200',
  };
  
  return (
    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${styles[status] || styles.PENDING}`}>
      {status.toLowerCase()}
    </span>
  );
}

export default function Dashboard() {
  const { user } = useAuthStore();

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['user-stats'],
    queryFn: () => api.get<{ stats: Stats }>('/users/stats'),
  });

  // Fetch activity
  const { data: activityData, isLoading: activityLoading } = useQuery({
    queryKey: ['user-activity'],
    queryFn: () => api.get<{ activity: ActivityItem[] }>('/users/activity?limit=15'),
  });

  const stats = statsData?.stats;
  const groupedActivity = useMemo(() => {
    if (!activityData?.activity) return {};
    return groupActivities(activityData.activity);
  }, [activityData]);

  const hasActivity = Object.keys(groupedActivity).length > 0;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header - Responsive */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6 lg:mb-8"
      >
        <h1 className="text-xl lg:text-2xl font-semibold text-slate-900 tracking-tight">
          {getGreeting()}, {user?.name?.split(' ')[0] || 'there'}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          {format(new Date(), 'EEEE, MMMM d')}
        </p>
      </motion.div>

      {/* Stats - Grid on mobile, inline on tablet/desktop */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="pb-6 mb-6 border-b border-slate-200"
      >
        {/* Mobile: Grid layout */}
        <div className="grid grid-cols-2 gap-3 md:hidden">
          <Link 
            to="/shop/products" 
            className="flex items-center gap-3 p-3 rounded-xl bg-white border border-gray-200 active:bg-gray-50"
          >
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
              <Package size={20} className="text-slate-600" />
            </div>
            <div>
              <p className="text-lg font-bold text-slate-900">{stats?.products || 0}</p>
              <p className="text-xs text-slate-500">Products</p>
            </div>
          </Link>
          <Link 
            to="/shop/orders" 
            className="flex items-center gap-3 p-3 rounded-xl bg-white border border-gray-200 active:bg-gray-50"
          >
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <ShoppingCart size={20} className="text-orange-600" />
            </div>
            <div>
              <p className="text-lg font-bold text-slate-900">{stats?.orders || 0}</p>
              <p className="text-xs text-slate-500">Orders</p>
            </div>
          </Link>
          <Link 
            to="/labels" 
            className="flex items-center gap-3 p-3 rounded-xl bg-white border border-gray-200 active:bg-gray-50"
          >
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <Tags size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-lg font-bold text-slate-900">{stats?.labels || 0}</p>
              <p className="text-xs text-slate-500">Labels</p>
            </div>
          </Link>
          {(stats?.revenue || 0) > 0 && (
            <Link 
              to="/shop/orders" 
              className="flex items-center gap-3 p-3 rounded-xl bg-green-50 border border-green-200 active:bg-green-100"
            >
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Euro size={20} className="text-green-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-green-700">
                  €{stats?.revenue.toLocaleString('de-DE', { minimumFractionDigits: 0 })}
                </p>
                <p className="text-xs text-green-600">Revenue</p>
              </div>
            </Link>
          )}
        </div>

        {/* Tablet/Desktop: Inline layout */}
        <div className="hidden md:flex items-center gap-6">
          <Link 
            to="/shop/products" 
            className="flex items-center gap-2 hover:text-slate-900 transition-colors group"
          >
            <Package size={16} className="text-slate-400 group-hover:text-slate-600" />
            <span className="text-sm text-slate-600 group-hover:text-slate-900">{stats?.products || 0} products</span>
          </Link>
          <div className="w-px h-4 bg-slate-200" />
          <Link 
            to="/shop/orders" 
            className="flex items-center gap-2 hover:text-slate-900 transition-colors group"
          >
            <ShoppingCart size={16} className="text-slate-400 group-hover:text-slate-600" />
            <span className="text-sm text-slate-600 group-hover:text-slate-900">{stats?.orders || 0} orders</span>
          </Link>
          <div className="w-px h-4 bg-slate-200" />
          <Link 
            to="/labels" 
            className="flex items-center gap-2 hover:text-slate-900 transition-colors group"
          >
            <Tags size={16} className="text-slate-400 group-hover:text-slate-600" />
            <span className="text-sm text-slate-600 group-hover:text-slate-900">{stats?.labels || 0} labels</span>
          </Link>
          {(stats?.revenue || 0) > 0 && (
            <>
              <div className="w-px h-4 bg-slate-200" />
              <Link 
                to="/shop/orders" 
                className="flex items-center gap-2 hover:text-green-700 transition-colors group"
              >
                <Euro size={16} className="text-green-500 group-hover:text-green-600" />
                <span className="text-sm font-medium text-green-600 group-hover:text-green-700">
                  {stats?.revenue.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                </span>
              </Link>
            </>
          )}
        </div>
      </motion.div>

      {/* Activity Feed */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xs lg:text-sm font-medium text-slate-900 uppercase tracking-wider">
            Recent Activity
          </h2>
          {hasActivity && (
            <span className="text-xs text-slate-400">
              {activityData?.activity.length} items
            </span>
          )}
        </div>

        {activityLoading ? (
          // Skeleton loader
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-start gap-3 animate-pulse">
                <div className="w-8 h-8 rounded-full bg-slate-100" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-slate-100 rounded w-1/3" />
                  <div className="h-3 bg-slate-100 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : hasActivity ? (
          <div className="space-y-6">
            {Object.entries(groupedActivity).map(([date, items]) => (
              <div key={date}>
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-3">
                  {date}
                </p>
                <div className="space-y-2">
                  {items.map((item, index) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Link
                        to={getActivityLink(item)}
                        className="flex items-start gap-3 group p-2 -mx-2 rounded-lg hover:bg-white hover:shadow-sm transition-all cursor-pointer"
                      >
                        <ActivityIcon type={item.type} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400">
                              {getActivityLabel(item.type)}
                            </span>
                            {item.type === 'order' && item.meta?.status && (
                              <OrderStatusBadge status={item.meta.status} />
                            )}
                          </div>
                          <p className="text-sm font-medium text-slate-900 truncate group-hover:text-slate-700">
                            {item.title}
                          </p>
                          <p className="text-xs text-slate-500">
                            {item.subtitle}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-slate-400 whitespace-nowrap">
                            {formatDistanceToNow(new Date(item.createdAt), { addSuffix: true })}
                          </span>
                          <ChevronRight size={14} className="text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Empty state
          <div className="text-center py-12">
            <Clock size={32} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-500">No activity yet</p>
            <p className="text-xs text-slate-400 mt-1 mb-4">
              Create your first product or label to get started
            </p>
            <div className="flex items-center justify-center gap-3">
              <Link
                to="/shop/products"
                className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 transition-colors"
              >
                <Package size={14} />
                Add product
              </Link>
              <span className="text-slate-300">•</span>
              <Link
                to="/labels"
                className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 transition-colors"
              >
                <Tags size={14} />
                Create label
              </Link>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
